import { List, Record } from 'immutable';
import { isArray, isObject, remove, uniq } from 'lodash';
import {
  DRAGGABLE_TYPE_UMO,
  DRAGGABLE_ORIGIN_UMO_GALLERY,
  DRAGGABLE_ORIGIN_UMO_GALLERY_WORKSPACE,
  DRAGGABLE_ORIGIN_WORKSPACE_PREVIEW,
  DRAGGABLE_ERROR_INVALID_ORIGIN,
  DRAGGABLE_ERROR_INVALID_TYPE,
} from '../constants/draggables';
import { SearchParams } from './searchUtils';

const dragGhostImage = (() => {
  const img = document.createElement('img');
  img.src = './images/images.png';
  return img;
})();
export const getDragGhostImage = () => dragGhostImage;

export class UMODraggable extends new Record(
  { type: DRAGGABLE_TYPE_UMO, umoList: [], origin: undefined, originData: {} }
) {
  constructor(umoList, origin, originData, isCopy = false) {
    if (
      origin !== DRAGGABLE_ORIGIN_UMO_GALLERY &&
      origin !== DRAGGABLE_ORIGIN_UMO_GALLERY_WORKSPACE &&
      origin !== DRAGGABLE_ORIGIN_WORKSPACE_PREVIEW
    ) {
      throw new Error(DRAGGABLE_ERROR_INVALID_ORIGIN);
    }
    super({
      umoList: isArray(umoList) ? new List(umoList) : new List([umoList]),
      origin,
      originData,
      isCopy,
    });
  }
  stringify() {
    return JSON.stringify(this);
  }
}

export class UMOGalleryUMODraggable extends UMODraggable {
  constructor(umoList, isWorkspace, originData, isCopy = false) {
    super(
      umoList,
      isWorkspace ? DRAGGABLE_ORIGIN_UMO_GALLERY_WORKSPACE : DRAGGABLE_ORIGIN_UMO_GALLERY,
      originData,
      isCopy
    );
  }
  isWorkspaceItem() {
    return this.origin === DRAGGABLE_ORIGIN_UMO_GALLERY_WORKSPACE;
  }

  /**
   * NOTE: when originData.isSelectAll is true, this function will return a list that will NEGATE
   * the "select all". i.e. select all umos EXCEPT the ones from getSelectionList. see
   * addToWorkspace action to see how it works.
   */
  getSelectionList() {
    // if isSelectAll, then this.umoList (which is assumed to be a single item array with the
    // dragged umo id will need to be removed from selectedUMOIDs since selectedUMOIDs will
    // be negated from the "select all". so the query turns out something like:
    //
    //   "select all umos in searchParams EXCEPT the ones in selectedUMOIDs BUT INCLUDING the
    //   dragged umo".
    if (this.getIsSelectAll()) {
      const selectedUMOIDsCopy =
        isArray(this.getSelectedUMOIDs()) ? [...this.getSelectedUMOIDs()] : [];

      remove(selectedUMOIDsCopy, umoID => this.umoList.indexOf(umoID) > -1);

      return selectedUMOIDsCopy;
    }

    return uniq([...this.umoList.toJS(), ...this.getSelectedUMOIDs()]);
  }

  getSearchParams() {
    if (this.originData && Object.prototype.hasOwnProperty.call(this.originData, 'searchParams')) {
      if (this.originData.searchParams instanceof SearchParams) {
        return this.originData.searchParams;
      } else if (isObject(this.originData.searchParams)) {
        return new SearchParams(this.originData.searchParams);
      }
      return this.originData.searchParams;
    }

    return undefined;
  }

  getSelectedUMOIDs() {
    if (
      this.originData &&
      Object.prototype.hasOwnProperty.call(this.originData, 'selectedUMOIDs') &&
      isArray(this.originData.selectedUMOIDs)
    ) {
      return this.originData.selectedUMOIDs;
    }

    return [];
  }

  getIsSelectAll() {
    if (this.originData && Object.prototype.hasOwnProperty.call(this.originData, 'isSelectAll')) {
      return this.originData.isSelectAll;
    }

    return false;
  }

  getCategoryID() {
    return this.originData?.categoryID;
  }
}

// not needed yet
//export class WorkspacePreviewUMODraggable extends UMODraggable {
//  constructor(umoList) {
//    super(umoList, DRAGGABLE_ORIGIN_WORKSPACE_PREVIEW);
//  }
//}

/**
 * Because the draggable classes defined in this file get stringified, this will convert to
 * appropriate draggable class instance.
 * @param draggableDataString The draggable data as a stringified plain object
 */
export function stringifiedToDraggable(draggableDataString) {
  const data = JSON.parse(draggableDataString);
  if (data && data.type === DRAGGABLE_TYPE_UMO) {
    if (
      data.origin === DRAGGABLE_ORIGIN_UMO_GALLERY ||
      data.origin === DRAGGABLE_ORIGIN_UMO_GALLERY_WORKSPACE
    ) {
      return new UMOGalleryUMODraggable(
        data.umoList,
        data.origin === DRAGGABLE_ORIGIN_UMO_GALLERY_WORKSPACE,
        data.originData,
        data.isCopy
      );
    }
    return new Error(DRAGGABLE_ERROR_INVALID_ORIGIN);
  }

  return new Error(DRAGGABLE_ERROR_INVALID_TYPE);
}
